import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import apiClient from 'src/helpers/apiClient';
import { getFormattedErr } from 'src/helpers';
import type { AssistantMoment } from 'src/helpers/assistantMoments';
import type { ExtensionStatus } from 'src/helpers/extensionBridge';
import type { SendAssistantMessageBodyPage } from 'src/openapi';
import type {
  AssistantConversation,
  AssistantMessage,
  AssistantNudge,
  AssistantPage,
  AssistantUsage,
} from 'src/redux/types/assistant';

const errorKey = (err: any): string =>
  err?.response?.data?.error ?? 'something_went_wrong';

export const sendAssistantMessage = createAsyncThunk(
  'assistant/sendMessage',
  async (
    {
      message,
      conversationId,
      page,
    }: { message: string; conversationId: string | null; page: AssistantPage },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiSendAssistantMessage({
        sendAssistantMessageBody: {
          message,
          conversation_id: conversationId,
          // The generated type reads ui_state's values as objects; they are strings.
          page: page as SendAssistantMessageBodyPage,
        },
      });
      return response.data as unknown as {
        conversation: AssistantConversation;
        user_message: AssistantMessage;
        assistant_message: AssistantMessage;
        usage: AssistantUsage;
      };
    } catch (err: any) {
      // The panel explains these in place; a toast would repeat it.
      const key = errorKey(err);
      if (
        ![
          'assistant_daily_limit_reached',
          'rescue_chat_limit_reached',
          'assistant_busy',
        ].includes(key)
      ) {
        enqueueSnackbar(getFormattedErr(key), { variant: 'error' });
      }
      return rejectWithValue(key);
    }
  },
);

export const fetchAssistantUsage = createAsyncThunk(
  'assistant/fetchUsage',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiGetAssistantUsage();
      return response.data as AssistantUsage;
    } catch (err: any) {
      return rejectWithValue(errorKey(err));
    }
  },
);

/** Today's plan, posted in the chat straight from data: no model round, and
 * no daily message. Asked again the same day, the one already posted. */
export const postDayPlan = createAsyncThunk(
  'assistant/postDayPlan',
  async ({ page }: { page: AssistantPage }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiPostDayPlan({
        postDayPlanBody: { page: page as SendAssistantMessageBodyPage },
      });
      return response.data as unknown as {
        conversation: AssistantConversation;
        message: AssistantMessage;
      };
    } catch (err: any) {
      // The panel explains a reply still on its way in place.
      const key = errorKey(err);
      if (key !== 'assistant_busy') {
        enqueueSnackbar(getFormattedErr(key), { variant: 'error' });
      }
      return rejectWithValue(key);
    }
  },
);

/** What the orb could say now -- about what the user just did (`moment`), or
 * as they arrive -- skipping today's; null for nothing. */
export const fetchAssistantNudge = createAsyncThunk(
  'assistant/fetchNudge',
  async (
    {
      skip,
      extension,
      moment,
    }: {
      skip: string[];
      extension: ExtensionStatus;
      moment?: AssistantMoment | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiGetAssistantNudge({
        skip,
        extension,
        moment: moment?.kind,
        subject: moment?.subject ?? undefined,
      });
      return (response.data.nudge ?? null) as AssistantNudge | null;
    } catch (err: any) {
      // A nudge is a nicety: one that didn't come needs no toast.
      return rejectWithValue(errorKey(err));
    }
  },
);

/** A page of every chat the user has had, the latest page without `before`. */
const readChat = async (before?: string) => {
  const response = await (
    await apiClient.getAssistantApi()
  ).assistantApiGetAssistantChat({ before });
  return {
    conversation: (response.data.conversation ??
      null) as AssistantConversation | null,
    messages: (response.data.messages ?? []) as AssistantMessage[],
    hasEarlier: !!response.data.has_earlier,
  };
};

export const fetchAssistantChat = createAsyncThunk(
  'assistant/fetchChat',
  async (_: void, { rejectWithValue }) => {
    try {
      return await readChat();
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(errorKey(err)), { variant: 'error' });
      return rejectWithValue(errorKey(err));
    }
  },
);

/** The page before `before`, the oldest message on screen. */
export const fetchEarlierAssistantMessages = createAsyncThunk(
  'assistant/fetchEarlierMessages',
  async (before: string, { rejectWithValue }) => {
    try {
      return await readChat(before);
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(errorKey(err)), { variant: 'error' });
      return rejectWithValue(errorKey(err));
    }
  },
);

/** The socket fallback: re-reads one reply while its pushes are not arriving. */
export const pollAssistantMessage = createAsyncThunk(
  'assistant/pollMessage',
  async (messageId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiGetAssistantMessage({ messageId });
      return response.data as unknown as AssistantMessage;
    } catch (err: any) {
      return rejectWithValue(errorKey(err));
    }
  },
);

export const decideAssistantAction = createAsyncThunk(
  'assistant/decideAction',
  async (
    {
      messageId,
      actionId,
      decision,
    }: { messageId: string; actionId: string; decision: 'confirm' | 'cancel' },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiDecideAssistantAction({
        messageId,
        actionId,
        decideAssistantActionBody: { decision },
      });
      return response.data as unknown as AssistantMessage;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(errorKey(err)), { variant: 'error' });
      return rejectWithValue(errorKey(err));
    }
  },
);

/**
 * Asks for the chat the app opens after a Pro checkout is left unfinished.
 * The server decides whether one opens (web, not Pro, not one already today):
 * `conversation` is null when it does not. Silent either way -- the buyer
 * did not ask for it.
 */
export const startCheckoutRescue = createAsyncThunk(
  'assistant/startCheckoutRescue',
  async (
    { reason, path }: { reason?: string; path: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiStartCheckoutRescue({
        startCheckoutRescueBody: { reason, page: { path } },
      });
      return response.data as unknown as {
        conversation: AssistantConversation | null;
        messages: AssistantMessage[];
      };
    } catch (err: any) {
      return rejectWithValue(errorKey(err));
    }
  },
);
